import { useCallback, useState } from 'react'
import Cropper, { type Area } from 'react-easy-crop'
import { getCroppedImageFile } from '../lib/crop-image'

export function PhotoCropModal({
  imageSrc,
  title = 'Crop photo',
  aspect = 3 / 4,
  onCancel,
  onConfirm,
}: {
  imageSrc: string
  title?: string
  aspect?: number
  onCancel: () => void
  onConfirm: (file: File) => Promise<void>
}) {
  const [crop, setCrop] = useState({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null)
  const [busy, setBusy] = useState(false)

  const onCropComplete = useCallback((_area: Area, pixels: Area) => {
    setCroppedAreaPixels(pixels)
  }, [])

  const apply = async () => {
    if (!croppedAreaPixels) return
    setBusy(true)
    try {
      const file = await getCroppedImageFile(imageSrc, croppedAreaPixels)
      await onConfirm(file)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="modal-backdrop photo-crop-backdrop" onClick={onCancel}>
      <div className="modal photo-crop-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-label={title}>
        <header className="photo-crop-head">
          <h2>{title}</h2>
          <button type="button" className="profile-viewer-close" onClick={onCancel} aria-label="Close">
            ✕
          </button>
        </header>
        <div className="photo-crop-stage">
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={aspect}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
            objectFit="contain"
          />
        </div>
        <label className="photo-crop-zoom">
          <span>Zoom</span>
          <input
            type="range"
            min={1}
            max={3}
            step={0.05}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
          />
        </label>
        <div className="modal-actions">
          <button type="button" className="button primary" disabled={busy || !croppedAreaPixels} onClick={() => void apply()}>
            {busy ? 'Saving…' : 'Use photo'}
          </button>
          <button type="button" className="button secondary" disabled={busy} onClick={onCancel}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  )
}
